// reducer.js — manages the event timeline state.
//
// State:
//   { events: [...] sorted by seq, lastSeq: number, replaying: bool, status: "idle" }
//
// Actions:
//   {type: "RESET"}                                  — clear timeline (e.g., switching threads)
//   {type: "REPLAY_START"}
//   {type: "REPLAY_END", upToSeq}
//   {type: "EVENT", envelope}                        — append/replace by seq + event_id
//   {type: "STATUS", status, stopReason}
//   {type: "OPTIMISTIC_USER_MESSAGE", text, idempotencyKey}
//   {type: "RECONCILE_OPTIMISTIC", idempotencyKey, canonical}
//   {type: "PREVIEW_DELTA", eventId, text}          — live text fragment of an in-flight agent.message

export const initialState = {
  events: [],
  lastSeq: 0,
  replaying: false,
  // false until the first replay completes — lets the UI show a loading
  // indicator (not the empty "send a message" state) while history arrives.
  loaded: false,
  status: "idle",
  stopReason: null,
  // event_id -> accumulated preview text of agent.messages still generating.
  // Dropped when the buffered message lands (same event_id) or the model
  // request ends without one (error / interrupt).
  previews: {},
};

export function reducer(state, action) {
  switch (action.type) {
    case "RESET":
      return { ...initialState };
    case "REPLAY_START":
      return { ...state, replaying: true };
    case "REPLAY_END":
      return { ...state, replaying: false, loaded: true, lastSeq: Math.max(state.lastSeq, action.upToSeq || 0) };
    case "EVENT": {
      const e = action.envelope;
      // Dedup: if an event with this seq+event_id already exists, ignore.
      // Comparing _optimistic too means an optimistic placeholder can't
      // accidentally absorb its canonical counterpart (or vice versa); the
      // canonical event will land separately and RECONCILE_OPTIMISTIC removes
      // the placeholder by idempotencyKey.
      const idx = state.events.findIndex(
        (x) =>
          x.seq === e.seq &&
          (x.event_id || null) === (e.event_id || null) &&
          !!x._optimistic === !!e._optimistic
      );
      let nextEvents = state.events;
      if (idx === -1) {
        // Insert in sorted order. Most events arrive in increasing seq order; binary
        // search-ish from the end for normal flows.
        nextEvents = [...state.events];
        let i = nextEvents.length - 1;
        while (i >= 0 && nextEvents[i].seq > e.seq) i--;
        nextEvents.splice(i + 1, 0, e);
      }
      // Bridge-originated envelopes (e.g. session.error for a failed upstream)
      // carry no seq. Math.max(n, undefined) is NaN, which then poisoned every
      // reconnect (?last_seq=NaN) and the HTTP catch-up (after_seq=NaN -> 400).
      const lastSeq = Number.isFinite(e.seq) ? Math.max(state.lastSeq, e.seq) : state.lastSeq;
      let previews = state.previews;
      if (e.type === "agent.message" && previews[e.event_id] !== undefined) {
        previews = { ...previews };
        delete previews[e.event_id];
      } else if (e.type === "span.model_request_end" && Object.keys(previews).length) {
        previews = {}; // no more deltas for this request; the buffered message either landed or never will
      }
      return { ...state, events: nextEvents, lastSeq, previews };
    }
    case "PREVIEW_DELTA": {
      if (!action.eventId || !action.text) return state;
      const prev = state.previews[action.eventId] || "";
      return { ...state, previews: { ...state.previews, [action.eventId]: prev + action.text } };
    }
    case "STATUS": {
      // `idle` with stop_reason requires_action is not idle: the agent is
      // waiting on a tool result we (self-hosted built-ins, env_*/remember) or
      // the user (always_ask approval) still owe it. Treating it as idle made
      // the header flip idle/running around every tool call and swapped the
      // Stop button for Send mid-turn. Keep the stopReason so the approval UI
      // can still find the blocked tool ids.
      const sr = action.stopReason ?? null;
      const status = (action.status === "idle" && sr && sr.type === "requires_action")
        ? "running" : action.status;
      return { ...state, status, stopReason: sr };
    }
    case "OPTIMISTIC_USER_MESSAGE": {
      const placeholder = {
        seq: state.lastSeq + 0.5, // floats sort between
        type: "user.message",
        event_id: action.idempotencyKey,
        thread_id: null,
        payload: { text: action.text, attachments: action.attachments || [] },
        _optimistic: true,
      };
      // Optimistically flip to "running" the instant the user sends: the working
      // indicator + stop button appear immediately instead of waiting ~2-3s for
      // session.status_running over the WS. The real status events reconcile it
      // (and sendMessage resets to idle if the send fails). Fresh turn → clear
      // any prior stop reason — EXCEPT a requires_action pause: the new message
      // queues behind the blocked tool, so the Approve/Deny buttons must stay.
      const sr = state.stopReason;
      return {
        ...state,
        events: [...state.events, placeholder],
        status: "running",
        stopReason: sr && sr.type === "requires_action" ? sr : null,
      };
    }
    case "RECONCILE_OPTIMISTIC": {
      const events = state.events.filter(
        (e) => !(e._optimistic && e.event_id === action.idempotencyKey)
      );
      return { ...state, events };
    }
    default:
      return state;
  }
}
